import { useState, useRef, useEffect } from "react";
import { arucoToSVGString } from "aruco-marker";

function AddCar() {
  const [selectedId, setSelectedId] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [digitalDraw, setDigitalDraw] = useState(false);
  const canvasRef = useRef(null);

  // Generate ID options (0-999)
  const idOptions = Array.from({ length: 1000 }, (_, i) => i);

  // Convert SVG string to canvas
  const svgToCanvas = (svgString, size) => {
    return new Promise((resolve) => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");

      // Disable image smoothing for crisp edges
      ctx.imageSmoothingEnabled = false;

      const img = new Image();
      const blob = new Blob([svgString], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);

      img.onload = () => {
        ctx.drawImage(img, 0, 0, size, size);
        URL.revokeObjectURL(url);
        resolve(canvas);
      };
      img.src = url;
    });
  };

  const addMarkersToImage = async (imageDataUrl, baseId) => {
    const img = new Image();
    img.onload = async () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");

      // Disable image smoothing for crisp marker edges
      ctx.imageSmoothingEnabled = false;

      // Draw original image
      ctx.drawImage(img, 0, 0);

      // Generate markers for the 4 corners
      const markerSize = Math.min(img.width, img.height) * 0.1;
      const safeSpace = digitalDraw ? markerSize * 0.15 : 0; // 15% of marker size as safe space

      const markers = [
        baseId * 4, // Top-left
        baseId * 4 + 1, // Top-right
        baseId * 4 + 2, // Bottom-right
        baseId * 4 + 3, // Bottom-left
      ];

      const positions = [
        { x: safeSpace, y: safeSpace },
        { x: img.width - markerSize - safeSpace, y: safeSpace },
        {
          x: img.width - markerSize - safeSpace,
          y: img.height - markerSize - safeSpace,
        },
        { x: safeSpace, y: img.height - markerSize - safeSpace },
      ];

      // Draw each marker using arucoToSVGString
      for (let index = 0; index < markers.length; index++) {
        const markerId = markers[index];
        const svgString = arucoToSVGString(markerId);
        const markerCanvas = await svgToCanvas(svgString, markerSize);
        ctx.drawImage(markerCanvas, positions[index].x, positions[index].y);
      }

      // Get the processed image
      const processedDataUrl = canvas.toDataURL("image/png");
      setProcessedImage(processedDataUrl);
    };
    img.src = imageDataUrl;
  };

  useEffect(() => {
    if (imagePreview && selectedId !== "") {
      addMarkersToImage(imagePreview, parseInt(selectedId));
    } else {
      setProcessedImage(null);
    }
  }, [imagePreview, selectedId, digitalDraw]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = () => {
    if (selectedId && processedImage) {
      // Create download link
      const link = document.createElement("a");
      link.href = processedImage;
      link.download = `car-id-${selectedId}-with-markers.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      alert(`Auto mit ID ${selectedId} wird heruntergeladen!`);

      // Reset image upload
      setImageFile(null);
      setImagePreview(null);
      setProcessedImage(null);
    }
  };

  return (
    <div className="addcar-container">
      <canvas ref={canvasRef} style={{ display: "none" }} />
      <div className="addcar-card">
        <h2 className="addcar-title">Fahrzeugtemplate erstellen</h2>

        {/* ID Dropdown */}
        <div className="addcar-form-group">
          <label className="addcar-label" htmlFor="car-id">
            Fahrzeug-ID auswählen
          </label>
          <select
            id="car-id"
            className="addcar-select"
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            <option value="">Bitte wählen Sie eine ID...</option>
            {idOptions.map((id) => (
              <option key={id} value={id}>
                ID: {id}
              </option>
            ))}
          </select>
        </div>

        {/* Digital Draw Checkbox */}
        <div className="addcar-form-group">
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={digitalDraw}
              onChange={(e) => setDigitalDraw(e.target.checked)}
              style={{
                width: "1.2rem",
                height: "1.2rem",
                cursor: "pointer",
                accentColor: "#00ccff",
              }}
            />
            <span className="addcar-label" style={{ margin: 0 }}>
              Digital Draw (Sicherheitsabstand um Marker)
            </span>
          </label>
        </div>

        {/* Image Upload */}
        <div className="addcar-form-group">
          <label className="addcar-label">Fahrzeugbild hochladen</label>
          <div style={{ position: "relative", width: "100%" }}>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              id="image-upload"
              style={{
                position: "absolute",
                width: "100%",
                height: "100%",
                opacity: 0,
                cursor: "pointer",
                zIndex: 2,
              }}
            />
            <label
              htmlFor="image-upload"
              style={{
                display: "block",
                padding: "0.875rem 1.5rem",
                fontSize: "clamp(0.9rem, 2.5vw, 1rem)",
                cursor: "pointer",
                backgroundColor: "#2c2c2e",
                color: "#ffffff",
                border: "2px solid #3c3c3e",
                borderRadius: "12px",
                fontWeight: "600",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                transition: "all 0.2s ease",
                textAlign: "center",
                userSelect: "none",
                WebkitTapHighlightColor: "transparent",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#3c3c3e";
                e.currentTarget.style.borderColor = "#00ccff";
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 4px 12px rgba(0, 204, 255, 0.3)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#2c2c2e";
                e.currentTarget.style.borderColor = "#3c3c3e";
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 2px 8px rgba(0, 0, 0, 0.3)";
              }}
            >
              {imageFile ? imageFile.name : "Datei hochladen"}
            </label>
          </div>

          {/* Image Preview with Markers */}
          {processedImage && (
            <div className="addcar-preview">
              <img
                src={processedImage}
                alt="Preview with markers"
                className="addcar-preview-image"
              />
            </div>
          )}
        </div>

        {/* Submit Button */}
        <button
          className="addcar-submit-btn"
          onClick={handleSubmit}
          disabled={!selectedId || !processedImage}
        >
          Bild herunterladen
        </button>
      </div>
    </div>
  );
}

export default AddCar;
